import * as React from 'react';
import { Box, Typography, Chip } from '@mui/material';
import { TrendingUpOutlined, ScheduleOutlined } from '@mui/icons-material';

import { MainPadding } from '../../components/MainPadding';
import { AppSection } from '../../components/AppSection';
import { RoleSection } from '../../components/RoleSection';
import { CardsDashboard } from './components/CardsDashboard';
import { serviceLocator } from '../../services';

const authenticationService = serviceLocator.getAuthenticationService();

export class Home extends React.Component<any, any> {

    public render() {
        const authUserData = authenticationService.getAuthenticatedUserData();
        const username = authUserData?.username ?? '';
        const greeting = greetingFor(new Date());

        return (
            <MainPadding>
                <AppSection section="home" />

                {/* Cinematic editorial hero. */}
                <Box
                    sx={{
                        position: 'relative',
                        overflow: 'hidden',
                        borderRadius: '22px',
                        padding: { xs: 3.5, md: 5 },
                        color: '#f4f6ff',
                        background:
                            'radial-gradient(800px 400px at 8% -30%, rgba(250, 135, 28, 0.28), transparent 60%),' +
                            'radial-gradient(700px 500px at 95% 0%, rgba(94, 124, 255, 0.30), transparent 55%),' +
                            'linear-gradient(135deg, #03124a 0%, #051a5e 60%, #04154f 100%)',
                        boxShadow: '0 30px 80px -30px rgba(2, 10, 48, 0.6)',
                        border: '1px solid rgba(255, 255, 255, 0.08)',
                    }}
                >
                    {/* Faint editorial grid texture. */}
                    <Box
                        aria-hidden
                        sx={{
                            position: 'absolute',
                            inset: 0,
                            pointerEvents: 'none',
                            opacity: 0.4,
                            maskImage: 'linear-gradient(180deg, black 0%, transparent 80%)',
                            WebkitMaskImage: 'linear-gradient(180deg, black 0%, transparent 80%)',
                            backgroundImage:
                                'linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px),' +
                                'linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)',
                            backgroundSize: '56px 56px',
                        }}
                    />

                    <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <Box
                            component="span"
                            sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 0.75,
                                paddingInline: 1.25,
                                paddingBlock: 0.5,
                                borderRadius: 999,
                                border: '1px solid rgba(250, 135, 28, 0.30)',
                                background: 'rgba(250, 135, 28, 0.08)',
                                color: 'rgba(255, 180, 92, 0.95)',
                                fontFamily: '"JetBrains Mono", monospace',
                                fontSize: 11,
                                fontWeight: 500,
                                letterSpacing: '0.16em',
                                textTransform: 'uppercase',
                            }}
                        >
                            <Box
                                component="span"
                                sx={{
                                    width: 6,
                                    height: 6,
                                    borderRadius: '50%',
                                    background: '#FA871C',
                                    boxShadow: '0 0 10px rgba(250, 135, 28, 0.65)',
                                }}
                                aria-hidden
                            />
                            {greeting}
                        </Box>
                    </Box>

                    <Typography
                        sx={{
                            position: 'relative',
                            fontFamily: '"Inter Tight", "Inter", sans-serif',
                            fontWeight: 700,
                            letterSpacing: '-0.022em',
                            lineHeight: 1.05,
                            fontSize: { xs: 36, sm: 44, md: 56 },
                            color: '#ffffff',
                            maxWidth: 720,
                        }}
                    >
                        Welcome back{username ? ',' : '.'}{' '}
                        {username && (
                            <Box
                                component="span"
                                sx={{
                                    background: 'linear-gradient(135deg, #ffd1a0 0%, #FA871C 60%, #ffb45c 100%)',
                                    WebkitBackgroundClip: 'text',
                                    WebkitTextFillColor: 'transparent',
                                    backgroundClip: 'text',
                                }}
                            >
                                {username}.
                            </Box>
                        )}
                    </Typography>

                    <Typography
                        sx={{
                            position: 'relative',
                            mt: 2,
                            color: 'rgba(244, 246, 255, 0.78)',
                            fontSize: { xs: 15, md: 17 },
                            lineHeight: 1.55,
                            maxWidth: 640,
                        }}
                    >
                        Your control room for every Hugo build, agency, and deploy. Pick a section from the rail on the left to dive in, or scan the latest signals below.
                    </Typography>

                    <Box sx={{ position: 'relative', mt: 4, display: 'flex', flexWrap: 'wrap', gap: 1.25 }}>
                        <Chip
                            icon={<TrendingUpOutlined sx={{ fontSize: 16 }} />}
                            label="Pipeline · live"
                            sx={chipSx}
                        />
                        <Chip
                            icon={<ScheduleOutlined sx={{ fontSize: 16 }} />}
                            label="Scheduled checks · hourly"
                            sx={chipSx}
                        />
                        <Chip label="Themes · Custom / Bootstrap 5.2" sx={chipSx} />
                    </Box>
                </Box>

                <RoleSection role="admin">
                    <Box sx={{ mt: 6 }}>
                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, mb: 2.5 }}>
                            <Typography
                                className="eyebrow"
                                sx={{ color: 'text.secondary' }}
                            >
                                Quality · last sync
                            </Typography>
                            <Box sx={{ flex: 1, height: 1, background: 'linear-gradient(90deg, rgba(11, 18, 48, 0.10), transparent)' }} />
                        </Box>
                        <Typography
                            variant="h5"
                            sx={{
                                fontFamily: '"Inter Tight", "Inter", sans-serif',
                                fontWeight: 700,
                                letterSpacing: '-0.014em',
                                mb: 2,
                            }}
                        >
                            Website quality signals
                        </Typography>
                        <CardsDashboard />
                    </Box>
                </RoleSection>
            </MainPadding>
        );
    }
}

const chipSx = {
    background: 'rgba(255, 255, 255, 0.06)',
    borderRadius: 999,
    border: '1px solid rgba(255, 255, 255, 0.10)',
    color: 'rgba(244, 246, 255, 0.88)',
    fontWeight: 500,
    letterSpacing: '-0.003em',
    height: 30,
    '& .MuiChip-icon': { color: 'rgba(255, 180, 92, 0.92)', marginLeft: 1 },
} as const;

function greetingFor(date: Date): string {
    const h = date.getHours();
    if (h < 5 || h >= 21) return 'Late shift';
    if (h < 12) return 'Good morning';
    if (h < 17) return 'Good afternoon';
    return 'Good evening';
}
